//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { isUndefined } from '@utils/filterPredicates';

/**
 * Replaces string of pixel specs with number of pixels.
 * @param   str         The string of pixel specs.
 * @returns {number}    The number of pixels or 0 if ${str} is not in correct format.
 * @example pxStrToNum('944px') = 944
 * @example pxStrToNum('944') = 0
 * @example pxStrToNum(944) = 0
 */
export function pxStrToNum(str: string) {
  if (typeof str === 'string' && str.endsWith('px')) {
    return +str.slice(0, -2);
  } else {
    return 0;
  }
}

/**
 * Checks if the given string is a valid non transparent hex color with
 * 6 characters (excluding the # symbol).
 *
 * @param color Color string to check;
 * @returns `true` if the color is a valid hex color, `false` otherwise
 */
export const isHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6})$/i.test(color);
};

/**
 * Checks if the given string is a valid non transparent hex color with
 * 3 characters (excluding the # symbol).
 *
 * @param color Color string to check;
 * @returns `true` if the color is a valid hex color, `false` otherwise
 */
export const isShorthandHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{3})$/i.test(color);
};

/**
 * Determines if text should be dark based on background color luminance
 * @param backgroundColor - 3 or 6 character Hex color string, including #
 * Good Examples - #FFAACC, #FAC
 * Bad Examples - FFgg-144, FZ-
 * Adapted from https://stackoverflow.com/a/41491220
 * @returns `true` if dark text should be used, `false` if dark text should not be used,
 *           and `undefined` if `backgroundColor` is not a valid hex color string
 */
export const shouldUseDarkText = (backgroundColor: string | undefined) => {
  if (
    isUndefined(backgroundColor) ||
    (!isHexColor(backgroundColor) && !isShorthandHexColor(backgroundColor))
  ) {
    return undefined;
  }

  let hex = backgroundColor.replace('#', '');
  if (hex.length === 3) {
    hex = [0, 1, 2].map((index) => hex.charAt(index).repeat(2)).join('');
  }
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const toSRGB = (value: number) =>
    value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);

  const luminance =
    0.2126 * toSRGB(r) + 0.7152 * toSRGB(g) + 0.0722 * toSRGB(b);

  return luminance > 0.179;
};
